
import { useToast } from "@/hooks/use-toast";
import { useState } from "react";
import { Subscription } from "./types";
import { StatusBadge } from "./components/StatusBadge";
import { AutoRenewalToggle } from "./components/AutoRenewalToggle";
import { ProductLogo } from "./components/ProductLogo";
import { ProductInfo } from "./components/ProductInfo";
import { SubscriptionDetails } from "./components/SubscriptionDetails";
import { ActionButtons } from "./components/ActionButtons";

interface InfradigitalConfig {
  bill_url: string;
  biller_code: string;
}

interface SubscriptionCardProps {
  subscription: Subscription;
  infradigitalConfig: InfradigitalConfig | null;
  onCancelClick: (id: string) => void;
  onAutoRenewalToggle?: (id: string, currentValue: boolean) => Promise<void>;
  isPublicView?: boolean;
}

export function SubscriptionCard({ 
  subscription, 
  infradigitalConfig, 
  onCancelClick,
  onAutoRenewalToggle,
  isPublicView = false
}: SubscriptionCardProps) {
  // Safely check if bills exists and has items before trying to use 'some'
  const hasUnpaidBills = subscription.bills && Array.isArray(subscription.bills) 
    ? subscription.bills.some(bill => bill.status === 'unpaid') 
    : false;
    
  const { toast } = useToast();
  
  // Get the payment URL if we have unpaid bills and the necessary configuration
  const getPaymentUrl = () => {
    if (hasUnpaidBills && infradigitalConfig && subscription.school_id) {
      return `${infradigitalConfig.bill_url}?biller_code=${infradigitalConfig.biller_code}&bill_key=${subscription.school_id}`;
    }
    return null;
  };

  const handleAutoRenewalToggle = async (id: string, currentValue: boolean) => {
    if (!onAutoRenewalToggle) return;
    
    try {
      await onAutoRenewalToggle(id, currentValue);
      
      toast({
        title: "Auto-renewal updated",
        description: !currentValue 
          ? "Your subscription will automatically renew when it expires" 
          : "Your subscription will not renew automatically",
      });
    } catch (error) {
      console.error('Failed to toggle auto-renewal:', error);
      toast({
        variant: "destructive",
        title: "Failed to update",
        description: "Could not update auto-renewal setting",
      });
      throw error; // Re-throw to signal failure to the component
    }
  };

  const paymentUrl = getPaymentUrl();

  return (
    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden mb-4">
      <div className="p-6">
        <div className="flex justify-between">
          <div>
            <StatusBadge 
              status={subscription.status} 
              hasUnpaidBills={hasUnpaidBills} 
              endDate={subscription.end_date} 
            />
          </div>
          
          {(['active', 'pending', 'past_due'].includes(subscription.status)) && onAutoRenewalToggle && (
            <AutoRenewalToggle
              isEnabled={subscription.auto_renewal}
              subscriptionId={subscription.id}
              onToggle={handleAutoRenewalToggle}
            />
          )}
        </div>
        
        <div className="grid grid-cols-3 gap-4 mt-4">
          <ProductLogo 
            logo={subscription.plan?.product?.logo} 
            fallbackText={subscription.plan?.product?.name || 'Subscription'} 
          />
          
          <div className="flex-1">
            <ProductInfo 
              logo={subscription.plan?.product?.logo}
              productName={subscription.plan?.product?.name || 'Unknown Product'}
              planName={subscription.plan?.name || 'Unknown Plan'}
              description={subscription.plan?.product?.description || ''}
            />
            
            <SubscriptionDetails 
              price={subscription.plan?.price || 0}
              startDate={subscription.start_date}
              endDate={subscription.end_date}
            />
          </div>
          
          <ActionButtons 
            paymentUrl={paymentUrl}
            status={subscription.status}
            subscriptionId={subscription.id}
            onCancelClick={onCancelClick}
            schoolId={subscription.school_id}
            infradigitalConfig={infradigitalConfig}
            hasUnpaidBills={hasUnpaidBills}
          />
        </div>
      </div>
    </div>
  );
}
